/**
 * LinkedIn Sharing Utility
 * Generates professional LinkedIn posts for award recognitions
 */

import { Nomination } from '../types/nomination';

/**
 * Generate a professional LinkedIn post text for an award recognition
 * @param nomination - The nomination data
 * @param sharerName - Optional name of the person sharing (defaults to generic message)
 */
export const generateLinkedInPost = (nomination: Nomination, sharerName?: string): string => {
  const { nomineeName, awardType, reason, supportingDetails, nominatorName } = nomination;
  
  // Award-specific emojis and hashtags
  const awardEmojis: Record<string, string> = {
    'SPOT Award': '⭐',
    'Quarterly Excellence Award': '🏆',
  };
  
  const emoji = awardEmojis[awardType] || '🏆';
  
  // Build the post
  const lines: string[] = [];
  
  // Opening line with emoji - personalized if sharer name provided
  if (sharerName) {
    lines.push(`${emoji} Excited to share that my colleague ${nomineeName} has been recognized with the ${awardType}! ${emoji}`);
  } else {
    lines.push(`${emoji} Proud to share that ${nomineeName} has been recognized with the ${awardType}! ${emoji}`);
  }
  lines.push('');
  
  // Recognition reason
  lines.push('🌟 Why this recognition matters:');
  lines.push(reason);
  lines.push('');
  
  // Supporting details if available
  if (supportingDetails) {
    lines.push('💡 Impact & Achievements:');
    lines.push(supportingDetails);
    lines.push('');
  }
  
  // Nominated by
  lines.push(`Nominated by: ${nominatorName}`);
  lines.push('');
  
  // Closing message
  lines.push('At Creyente InfoTech, we believe in celebrating excellence and recognizing those who go above and beyond. Congratulations on this well-deserved recognition! 🎉');
  lines.push('');
  
  // Hashtags
  const hashtags = [
    '#EmployeeRecognition',
    '#TeamExcellence',
    '#CreyenteInfoTech',
    '#AwardWinner',
    '#WorkplaceCulture',
    '#Congratulations',
  ];
  
  // Add award-specific hashtags
  if (awardType === 'SPOT Award') {
    hashtags.push('#SpotAward', '#InstantRecognition');
  } else if (awardType === 'Quarterly Excellence Award') {
    hashtags.push('#QuarterlyAward', '#QuarterlyExcellence');
  }
  
  lines.push(hashtags.join(' '));
  
  return lines.join('\n');
};

/**
 * Open LinkedIn share dialog with image URL
 * @param imageUrl - Optional URL of the generated award image
 * @param postText - The post text to share
 */
export const shareOnLinkedInWithImage = (imageUrl: string, postText: string) => {
  // Copy post text to clipboard
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(postText).then(() => {
      console.log('[LinkedIn] Post text copied to clipboard');
    }).catch(err => {
      console.error('[LinkedIn] Failed to copy to clipboard:', err);
    });
  }

  // LinkedIn share URL with image
  // Note: LinkedIn's share URL doesn't support pre-filled text or images via URL parameters
  // Users will need to paste the text and upload the image manually
  // However, we can open LinkedIn's post creation page
  const linkedInShareUrl = 'https://www.linkedin.com/feed/?shareActive=true';
  
  // Open LinkedIn in new window
  window.open(linkedInShareUrl, '_blank', 'width=700,height=700');
  
  // If image URL provided, also download it for easy upload
  if (imageUrl) {
    console.log('[LinkedIn] Image URL:', imageUrl);
    // Image will be shown in preview modal for download
  }
};

/**
 * Open LinkedIn share dialog with pre-filled post (legacy method)
 */
export const shareOnLinkedIn = (nomination: Nomination, sharerName?: string) => {
  const postText = generateLinkedInPost(nomination, sharerName);
  
  // LinkedIn share URL
  const linkedInShareUrl = 'https://www.linkedin.com/feed/?shareActive=true';
  
  // Copy to clipboard
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(postText).then(() => {
      console.log('[LinkedIn] Post text copied to clipboard');
    }).catch(err => {
      console.error('[LinkedIn] Failed to copy to clipboard:', err);
    });
  }
  
  // Open LinkedIn in new tab
  window.open(linkedInShareUrl, '_blank', 'width=700,height=700');
  
  return postText;
};

/**
 * Generate a shareable image URL for the nomination
 * This would ideally be a Lambda function that generates an image
 * For now, we'll use the nominee photo or a placeholder
 */
export const getShareableImageUrl = (nomination: Nomination): string | undefined => {
  return nomination.nomineePhotoUrl;
};

/**
 * Copy LinkedIn post to clipboard
 */
export const copyLinkedInPostToClipboard = async (nomination: Nomination): Promise<boolean> => {
  const postText = generateLinkedInPost(nomination);
  
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(postText);
      return true;
    }
    
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = postText;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    console.error('[LinkedIn] Failed to copy to clipboard:', err);
    return false;
  }
};
